/*
 * EventDetailPage — /events/:id (Iter 78)
 * ========================================
 * Full-info detail page for a single marketing event with RSVP inline.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Calendar as CalIcon, MapPin, Users, Euro, ArrowLeft, ExternalLink,
  CheckCircle2, Clock, Share2, Ticket, ChevronRight,
} from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BE = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  open_day: "Open Day", camp: "Camp", tournament: "Τουρνουά",
  webinar: "Webinar", social: "Κοινωνική", trial: "Δοκιμαστικά", other: "Εκδήλωση",
};
const TYPE_COLORS = {
  open_day: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  camp: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  tournament: "bg-[#F5A623]/15 text-[#F5A623] border-[#F5A623]/30",
  webinar: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  social: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  trial: "bg-red-500/15 text-red-300 border-red-500/30",
  other: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
};

const EventDetailPage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const [rsvpForm, setRsvpForm] = useState({
    name: "", email: "", phone: "", guests: 1, child_age: "", notes: "",
  });
  const [rsvpDone, setRsvpDone] = useState(false);
  const [rsvpErr, setRsvpErr] = useState("");
  const [rsvpBusy, setRsvpBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/marketing/events/${id}`)
      .then((r) => setEvent(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const submitRsvp = async (e) => {
    e.preventDefault();
    if (!rsvpForm.name.trim()) return setRsvpErr("Απαιτείται όνομα");
    setRsvpErr("");
    setRsvpBusy(true);
    try {
      await axios.post(`${API}/marketing/events/${id}/rsvp`, {
        name: rsvpForm.name.trim(),
        email: rsvpForm.email.trim() || null,
        phone: rsvpForm.phone.trim() || null,
        guests: Number(rsvpForm.guests) || 1,
        child_age: rsvpForm.child_age ? Number(rsvpForm.child_age) : null,
        notes: rsvpForm.notes.trim() || null,
      });
      setRsvpDone(true);
    } catch (err) {
      setRsvpErr(err.response?.data?.detail || "Σφάλμα εγγραφής.");
    } finally {
      setRsvpBusy(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: event?.title || "Εκδήλωση", url }); }
      catch (_e) { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); alert("Ο σύνδεσμος αντιγράφηκε!"); }
      catch (_e) { /* clipboard blocked */ }
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black pt-32 px-6 text-center text-zinc-500">Φόρτωση…</div>;
  }
  if (notFound || !event) {
    return (
      <div className="min-h-screen bg-black pt-32 px-6" data-testid="event-not-found">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="font-['Bebas_Neue'] text-4xl text-white mb-3">Δεν βρέθηκε</h1>
          <p className="text-zinc-400 mb-6">Η εκδήλωση δεν είναι διαθέσιμη.</p>
          <Link to="/events" className="inline-flex items-center gap-2 text-[#F5A623] hover:underline">
            <ArrowLeft size={14} /> Πίσω στις εκδηλώσεις
          </Link>
        </div>
      </div>
    );
  }

  const cover = event.cover_image_url
    ? (event.cover_image_url.startsWith("http") ? event.cover_image_url : `${BE}${event.cover_image_url}`)
    : null;
  const capacityLeft = event.capacity != null ? event.capacity - (event.rsvp_count || 0) : null;
  const sold_out = capacityLeft != null && capacityLeft <= 0;
  const scopeLabel = event.team_scope === "first_team" ? "Πρώτη Ομάδα"
                   : event.team_scope === "academy" ? "Ακαδημία" : "Όλοι";

  return (
    <div className="min-h-screen bg-black" data-testid="event-detail-page">
      <SEO
        title={`${event.title} · Εκδηλώσεις · Λευτεριά FC`}
        description={event.description?.slice(0, 160) || "Εκδήλωση της Λευτεριά FC"}
        image={cover}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-8 px-6 overflow-hidden">
        {cover && (
          <div className="absolute inset-0 -z-0 opacity-25">
            <img loading="lazy" decoding="async" src={cover} alt="" className="w-full h-full object-cover blur-sm" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
          </div>
        )}
        <div className="max-w-5xl mx-auto relative">
          <Link to="/events" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#F5A623] mb-4">
            <ArrowLeft size={12} /> Όλες οι εκδηλώσεις
          </Link>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`text-[10px] px-3 py-1 rounded-full border font-semibold ${TYPE_COLORS[event.event_type] || TYPE_COLORS.other}`}>
              {TYPE_LABELS[event.event_type] || "Εκδήλωση"}
            </span>
            <span className="text-[10px] px-3 py-1 rounded-full border border-[#262626] text-zinc-400">
              {scopeLabel}
            </span>
            {event.is_featured && (
              <span className="text-[10px] px-3 py-1 rounded-full bg-[#F5A623] text-black font-semibold uppercase tracking-widest">
                Featured
              </span>
            )}
          </div>

          <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl text-white tracking-tight leading-[0.95] mb-4"
            data-testid="event-title">
            {event.title}
          </h1>

          {/* Meta strip */}
          <div className="flex flex-wrap gap-4 md:gap-6 text-sm text-zinc-300 mt-6">
            <div className="flex items-center gap-2">
              <CalIcon size={16} className="text-[#F5A623]" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Ημερομηνία</p>
                <p>{new Date(event.start_at).toLocaleString("el-GR", { dateStyle: "full", timeStyle: "short" })}</p>
              </div>
            </div>
            {event.end_at && (
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#F5A623]" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Λήγει</p>
                  <p>{new Date(event.end_at).toLocaleString("el-GR", { dateStyle: "short", timeStyle: "short" })}</p>
                </div>
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-[#F5A623]" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Τοποθεσία</p>
                  <p>{event.location}</p>
                </div>
              </div>
            )}
            {event.capacity != null && (
              <div className="flex items-center gap-2">
                <Users size={16} className="text-[#F5A623]" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Θέσεις</p>
                  <p>{event.rsvp_count || 0} / {event.capacity}
                    {capacityLeft > 0 && capacityLeft <= 5 && (
                      <span className="text-amber-400 ml-1">— μόλις {capacityLeft} απομένουν</span>
                    )}
                  </p>
                </div>
              </div>
            )}
            {event.price_eur != null && (
              <div className="flex items-center gap-2">
                <Euro size={16} className="text-[#F5A623]" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Τιμή</p>
                  <p>{event.price_eur > 0 ? `${event.price_eur} €` : "Δωρεάν"}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cover image full-width */}
      {cover && (
        <section className="px-6 mt-4 mb-8">
          <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden border border-[#1e1e1e] aspect-video bg-[#0f0f0f]">
            <img loading="lazy" decoding="async" src={cover} alt={event.title}
              className="w-full h-full object-cover" data-testid="event-cover" />
          </div>
        </section>
      )}

      {/* Description + RSVP form side-by-side */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* Description */}
          <div>
            <h2 className="font-['Bebas_Neue'] text-2xl text-white tracking-wide mb-4">Λεπτομέρειες</h2>
            {event.description ? (
              <div className="prose prose-invert max-w-none">
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap" data-testid="event-description">
                  {event.description}
                </p>
              </div>
            ) : (
              <p className="text-zinc-500 text-sm italic">Δεν υπάρχει περιγραφή.</p>
            )}

            <button
              onClick={share}
              className="mt-6 inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#F5A623]"
              data-testid="event-share-btn"
            >
              <Share2 size={12} /> Κοινοποίηση
            </button>
          </div>

          {/* RSVP */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="bg-[#0a0a0a] border border-[#1e1e1e] rounded-xl overflow-hidden">
              <div className="p-5 border-b border-[#1e1e1e] bg-gradient-to-br from-[#F5A623]/10 to-transparent">
                <Ticket size={22} className="text-[#F5A623] mb-2" />
                <h3 className="font-['Bebas_Neue'] text-xl text-white tracking-wide">Δήλωση συμμετοχής</h3>
                <p className="text-xs text-zinc-500 mt-1">Θα σου στείλουμε λεπτομέρειες πριν την εκδήλωση.</p>
              </div>

              {event.status === "cancelled" ? <p className="p-5 text-red-300" role="status" data-testid="event-cancelled-status">Η εκδήλωση ακυρώθηκε. Δεν γίνονται νέες εγγραφές.</p> : sold_out ? (
                <div className="p-5 text-center">
                  <p className="text-zinc-400 text-sm mb-2">Οι θέσεις εξαντλήθηκαν.</p>
                  <Link to="/events" className="text-[#F5A623] text-xs hover:underline">
                    Δες άλλες εκδηλώσεις
                  </Link>
                </div>
              ) : event.registration_url ? (
                <div className="p-5">
                  <a href={event.registration_url} target="_blank" rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#F5A623] text-black text-sm font-semibold rounded hover:bg-[#e6951a]"
                    data-testid="event-external-registration">
                    Πλήρεις πληροφορίες <ExternalLink size={14} />
                  </a>
                </div>
              ) : rsvpDone ? (
                <div className="p-5 text-center">
                  <CheckCircle2 size={40} className="mx-auto text-emerald-400 mb-3" />
                  <h4 className="text-white font-semibold text-sm mb-1">Ολοκληρώθηκε!</h4>
                  <p className="text-zinc-400 text-xs">Θα σου στείλουμε σύντομα λεπτομέρειες.</p>
                </div>
              ) : (
                <form onSubmit={submitRsvp} className="p-5 space-y-3" data-testid="event-rsvp-form">
                  <input value={rsvpForm.name}
                    onChange={(e) => setRsvpForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ονοματεπώνυμο *"
                    className="w-full bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white"
                    required data-testid="event-rsvp-name" />
                  <input type="email" value={rsvpForm.email}
                    onChange={(e) => setRsvpForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="Email"
                    className="w-full bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white" />
                  <input type="tel" value={rsvpForm.phone}
                    onChange={(e) => setRsvpForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="Τηλέφωνο"
                    className="w-full bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" min={1} max={20} value={rsvpForm.guests}
                      onChange={(e) => setRsvpForm(f => ({ ...f, guests: e.target.value }))}
                      placeholder="Άτομα"
                      className="bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white" />
                    {event.event_type === "trial" || event.event_type === "camp" ? (
                      <input type="number" min={3} max={99} value={rsvpForm.child_age}
                        onChange={(e) => setRsvpForm(f => ({ ...f, child_age: e.target.value }))}
                        placeholder="Ηλικία παιδιού"
                        className="bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white" />
                    ) : <div />}
                  </div>
                  <textarea value={rsvpForm.notes}
                    onChange={(e) => setRsvpForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Σημειώσεις" rows={2}
                    className="w-full bg-black border border-[#262626] rounded px-3 py-2 text-sm text-white" />
                  {rsvpErr && <p className="text-red-400 text-xs" data-testid="event-rsvp-error">{rsvpErr}</p>}
                  <button type="submit" disabled={rsvpBusy}
                    className="w-full py-2.5 bg-[#F5A623] text-black text-sm font-semibold rounded hover:bg-[#e6951a] disabled:opacity-40"
                    data-testid="event-rsvp-submit">
                    {rsvpBusy ? "Αποστολή…" : "Εγγραφή"}
                  </button>
                </form>
              )}
            </div>

            <Link to="/events"
              className="mt-4 flex items-center justify-between text-xs text-zinc-400 hover:text-[#F5A623] px-1">
              Όλες οι εκδηλώσεις <ChevronRight size={12} />
            </Link>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default EventDetailPage;
